import type {
  EnrichedUserResponse,
  PatchUsersRequest,
  PatchUsersResponse,
  PatchUsersV2Response,
  PatchUsersV2TransformedResponse,
  UserInfo,
} from '@polyai/common/api/resources/userManagement/types';

import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';

export const getUsers = (accountId: string) => {
  return doGet<UserInfo[]>(
    generateNextPath(ENDPOINTS.USER_MANAGEMENT, {
      accountId,
    }),
    LOGGER_TYPE.USER_MANAGEMENT,
    { params: { account_id: accountId } },
  );
};

export const patchUsers = ({
  accountId,
  body,
}: {
  accountId: string;
  body: Omit<PatchUsersRequest, 'account_id'>;
}) => {
  return doPatch<PatchUsersResponse>(
    generateNextPath(ENDPOINTS.USER_MANAGEMENT, {
      accountId,
    }),
    LOGGER_TYPE.USER_MANAGEMENT,
    {
      ...body,
      account_id: accountId,
    },
  );
};

export const deleteUsers = ({
  accountId,
  userEmails,
}: {
  accountId: string;
  userEmails: string[];
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.USER_MANAGEMENT, {
      accountId,
    }),
    LOGGER_TYPE.USER_MANAGEMENT,
    {
      users_email: userEmails,
      account_id: accountId,
    },
  );
};

const INVITE_TAG_PREFIX = 'invite_expires:';

const transformEnrichedUser = (
  enrichedUser: EnrichedUserResponse,
  accountId: string,
): UserInfo => {
  const accountRole = enrichedUser.roles_per_account[accountId];
  const tags = (accountRole?.tags ?? []).filter(
    t => !t.startsWith(INVITE_TAG_PREFIX),
  );
  return {
    user_email: enrichedUser.user_email,
    account_id: accountId,
    role: accountRole?.role ?? '',
    tags,
    per_project_config: accountRole?.per_project_config ?? {},
    active: enrichedUser.active,
    invite_expires_at: enrichedUser.invite_expires_at,
  };
};

export const getEnrichedUsers = (accountId: string) => {
  return doGet<EnrichedUserResponse[]>(
    generateNextPath(ENDPOINTS.USER_MANAGEMENT, { accountId }),
    LOGGER_TYPE.USER_MANAGEMENT,
    { params: { account_id: accountId }, version: 'v2' },
  ).then(users => users.map(user => transformEnrichedUser(user, accountId)));
};

/**
 * Transform a v2 PATCH response (per-user results) into the v1
 * PatchUsersResponse shape so the existing UI (toasts, counts) works
 * unchanged. ERROR results are filtered out — only successfully
 * provisioned emails appear in `users_email`.
 */
export const transformPatchV2Response = (
  response: PatchUsersV2Response,
  accountId: string,
  body: Omit<PatchUsersRequest, 'account_id'>,
): PatchUsersV2TransformedResponse => {
  const successEmails = response.results
    .filter(r => r.status !== 'ERROR')
    .map(r => r.email);
  return {
    users_email: successEmails,
    account_id: accountId,
    role: body.role,
    tags: body.tags ?? [],
    per_project_config: body.per_project_config ?? {},
  };
};

export const patchUsersV2 = ({
  accountId,
  body,
}: {
  accountId: string;
  body: Omit<PatchUsersRequest, 'account_id'>;
}) => {
  return doPatch<PatchUsersV2Response>(
    generateNextPath(ENDPOINTS.USER_MANAGEMENT, { accountId }),
    LOGGER_TYPE.USER_MANAGEMENT,
    { ...body, account_id: accountId },
    { version: 'v2' },
  ).then(response => transformPatchV2Response(response, accountId, body));
};

export const resendInvite = ({
  accountId,
  email,
}: {
  accountId: string;
  email: string;
}) => {
  return doPost<{ email: string; resent: boolean }>(
    generateNextPath(ENDPOINTS.USER_MANAGEMENT_RESEND_INVITE, { accountId }),
    LOGGER_TYPE.USER_MANAGEMENT,
    { email, account_id: accountId },
    { version: 'v2' },
  );
};
